<template>
  <v-dialog
    v-model="internalShowModal"
    max-width="800px"
    scrollable
    persistent
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="text-h6">{{ isEditMode ? 'Edit Visual Identity System' : 'Create New Visual Identity System' }}</span>
        <action-button 
          variant="text"
          right-icon="mdi-close" 
          @click="closeModal"
        >
        </action-button>
      </v-card-title>
      
      <v-card-text class="pb-0">
        <v-stepper v-model="currentStep" color="#e94057">
          <v-stepper-header>
            <v-stepper-item value="1" title="Brand"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item value="2" title="Information"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item value="3" title="Images"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item value="4" title="3D Model"></v-stepper-item>
          </v-stepper-header>

          <v-stepper-window>
            <v-stepper-window-item value="1">
              <!-- Brand Selection -->
              <div class="create-section">
                <h3 class="text-subtitle-1 font-weight-bold mb-4">Brand Selection</h3>
                
                <div class="mb-4" v-if="!isEditMode">
                  <v-radio-group v-model="brandSelection" inline>
                    <v-radio label="Select Existing Brand" value="existing"></v-radio>
                    <v-radio label="Create New Brand" value="new"></v-radio>
                  </v-radio-group>
                </div>
                
                <v-slide-y-transition>
                  <div v-if="!isEditMode && brandSelection === 'existing' || isEditMode">
                    <v-select
                      v-model="formData.brand"
                      :items="brands"
                      item-title="name"
                      item-value="id"
                      label="Select Brand"
                      variant="outlined"
                      :loading="brandsLoading"
                      :error-messages="validationErrors.brand"
                      @update:model-value="clearValidationError('brand')"
                      :disabled="isEditMode"
                    >
                      <!-- Selected item display -->
                      <template #selection="{ item }">
                        <div class="d-flex align-center">
                          <v-avatar size="24" class="mr-2">
                            <v-img
                              v-if="item.raw.logo || item.raw.brand_logo"
                              :src="item.raw.logo || item.raw.brand_logo"
                            />
                            <span v-else class="avatar-initials">{{ getInitials(item.raw.name) }}</span>
                          </v-avatar>
                          <span>{{ item.raw.name }}</span>
                        </div>
                      </template>

                      <!-- Dropdown list items -->
                      <template #item="{ item, props }">
                        <v-list-item v-bind="props" :title="null" class="py-2">
                          <div class="d-flex align-center">
                            <v-avatar size="32" class="mr-2">
                              <v-img
                                v-if="item.raw.logo || item.raw.brand_logo"
                                :src="item.raw.logo || item.raw.brand_logo"
                              />
                              <span v-else class="avatar-initials">{{ getInitials(item.raw.name) }}</span>
                            </v-avatar>
                            <span>{{ item.raw.name }}</span>
                          </div>
                        </v-list-item>
                      </template>
                    </v-select>
                  </div>
                </v-slide-y-transition>
                
                <v-slide-y-transition>
                  <div v-if="!isEditMode && brandSelection === 'new'" class="new-brand-form">
                    <v-text-field
                      v-model="newBrand.name"
                      label="Brand Name"
                      variant="outlined"
                      :error-messages="validationErrors.brandName"
                      @input="clearValidationError('brandName')"
                    ></v-text-field>
                    
                    <v-textarea
                      v-model="newBrand.description"
                      label="Brand Description"
                      variant="outlined"
                      rows="3"
                    ></v-textarea>
                    
                    <v-file-input
                      v-model="newBrand.logo"
                      label="Brand Logo"
                      variant="outlined"
                      accept="image/*"
                      prepend-icon="mdi-camera"
                      show-size
                      :error-messages="validationErrors.brandLogo"
                      @update:model-value="clearValidationError('brandLogo')"
                    ></v-file-input>
                  </div>
                </v-slide-y-transition>
              </div>
            </v-stepper-window-item>
            
            <v-stepper-window-item value="2">
              <!-- VIS Basic Information -->
              <div class="create-section">
                <h3 class="text-subtitle-1 font-weight-bold mb-4">VIS Information</h3>
                
                <v-text-field
                  v-model="formData.version"
                  label="Version"
                  variant="outlined"
                  :error-messages="validationErrors.version"
                  @input="clearValidationError('version')"
                  hint="e.g., V1, V2.5, etc."
                ></v-text-field>
                
                <v-file-input
                  v-model="formData.document"
                  label="VIS PDF Document"
                  variant="outlined"
                  accept="application/pdf"
                  prepend-icon="mdi-file-pdf-box"
                  show-size
                  :error-messages="validationErrors.document"
                  @update:model-value="clearValidationError('document')"
                  class="mt-3"
                ></v-file-input>
                
                <v-menu
                  ref="dateMenu"
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ props }">
                    <v-text-field
                      v-model="formData.release_date"
                      label="Release Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="props"
                      variant="outlined"
                      :error-messages="validationErrors.release_date"
                      @input="clearValidationError('release_date')"
                      class="mt-3"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="formData.release_date"
                    @update:model-value="dateMenu = false"
                  ></v-date-picker>
                </v-menu>
                
                <v-textarea
                  v-model="formData.category"
                  label="Category"
                  variant="outlined"
                  rows="3"
                  class="mt-3"
                  :error-messages="validationErrors.category"
                  @input="clearValidationError('category')"
                ></v-textarea>
                
                <v-textarea
                  v-model="formData.market"
                  label="Market"
                  variant="outlined"
                  rows="3"
                  class="mt-3"
                  :error-messages="validationErrors.market"
                  @input="clearValidationError('market')"
                ></v-textarea>
                
                <v-select
                  v-model="formData.geographical_footprint"
                  :items="regions"
                  item-title="name"
                  item-value="id"
                  label="Geographical Regions"
                  variant="outlined"
                  multiple
                  chips
                  class="mt-3"
                  :loading="regionsLoading"
                  :error-messages="validationErrors.geographical_footprint"
                  @update:model-value="clearValidationError('geographical_footprint')"
                ></v-select>
              </div>
            </v-stepper-window-item>
            
            <v-stepper-window-item value="3">
              <!-- VIS Images -->
              <div class="create-section">
                <h3 class="text-subtitle-1 font-weight-bold mb-4">VIS Image Materials</h3>
                
                <div class="d-flex justify-space-between align-center mb-4">
                  <div>
                    <action-button
                      variant="primary"
                      left-icon="mdi-plus"
                      @click="openImageFileDialog"
                    >
                      Add Images
                    </action-button>
                    <input
                      type="file"
                      ref="imageFileInput"
                      accept="image/*"
                      multiple
                      style="display: none"
                      @change="handleImageFileSelect"
                    >
                  </div>
                  <div v-if="imageMaterials.length > 0" class="text-caption">
                    {{ imageMaterials.length }} image(s) selected
                  </div>
                </div>

                <!-- Drag and Drop Zone -->
                <div 
                  class="drag-drop-zone mb-4"
                  :class="{ 
                    'drag-over': isDragOver,
                    'has-files': imageMaterials.length > 0 
                  }"
                  @click="openImageFileDialog"
                  @drop="handleDrop"
                  @dragover="handleDragOver"
                  @dragenter="handleDragEnter"
                  @dragleave="handleDragLeave"
                >
                  <div class="drag-drop-content">
                    <v-icon size="48" color="grey-lighten-1" class="mb-2">mdi-cloud-upload</v-icon>
                    <div class="text-subtitle-1 font-weight-medium mb-1">
                      Drag & Drop Images Here
                    </div>
                    <div class="text-caption text-grey">
                      or click anywhere in this area to browse files
                    </div>
                    <div class="text-caption text-grey mt-1">
                      Supported formats: JPG, PNG, GIF, WebP
                    </div>
                  </div>
                </div>

                <!-- Existing Images (Edit Mode) -->
                <div v-if="isEditMode && existingImages.length > 0" class="mb-4">
                  <h4 class="text-subtitle-2 font-weight-medium mb-3">Current Images</h4>
                  <v-list class="bg-grey-lighten-5 rounded pa-0 mb-4">
                    <v-list-item
                      v-for="(image, index) in existingImages"
                      :key="`existing-${index}`"
                      :title="image.title"
                      :subtitle="`${formatMaterialType(image.material_type)}`"
                      class="image-list-item"
                    >
                      <template v-slot:prepend>
                        <div class="image-thumbnail">
                          <img :src="image.image" alt="Preview">
                        </div>
                      </template>
                      
                      <template v-slot:append>
                        <action-button
                          variant="text"
                          right-icon="mdi-delete"
                          @click="removeExistingImage(index)"
                          class="pa-1 ma-0"
                        >
                        </action-button>
                      </template>
                    </v-list-item>
                  </v-list>
                </div>
                
                <!-- New Images -->
                <v-list v-if="imageMaterials.length > 0" class="bg-grey-lighten-5 rounded pa-0 mb-4">
                  <div v-if="isEditMode" class="pa-2">
                    <h4 class="text-subtitle-2 font-weight-medium">New Images</h4>
                  </div>
                  <v-list-item
                    v-for="(file, index) in imageMaterials"
                    :key="`new-${index}`"
                    :title="file.name"
                    :subtitle="`${(file.size / 1024).toFixed(1)} KB`"
                    class="image-list-item"
                  >
                    <template v-slot:prepend>
                      <div class="image-thumbnail">
                        <img :src="imagePreviewUrls[index]" alt="Preview">
                      </div>
                    </template>
                    
                    <template v-slot:append>
                      <action-button
                        variant="text"
                        right-icon="mdi-delete"
                        @click="removeImage(index)"
                        class="pa-1 ma-0"
                      >
                      </action-button>
                    </template>
                  </v-list-item>
                </v-list>
                
                <v-alert
                  v-if="imageMaterials.length === 0 && (!isEditMode || existingImages.length === 0)"
                  type="info"
                  variant="tonal"
                  class="mb-4"
                >
                  No images selected. Please add at least one image.
                </v-alert>
                
                <div v-if="imageMaterials.length > 0">
                  <h4 class="text-subtitle-1 font-weight-medium mb-3">{{ isEditMode ? 'New ' : '' }}Image Details</h4>
                  
                  <div 
                    v-for="(preview, index) in imagePreviewUrls" 
                    :key="`preview-${index}`"
                    class="image-details-card mb-4 pa-3 bg-white rounded"
                  >
                    <div class="d-flex align-start">
                      <div class="mr-4">
                        <div class="image-preview-sm">
                          <img :src="preview" alt="Image Preview">
                        </div>
                        <div class="text-caption text-center mt-1">
                          {{ imageMaterials[index].name.length > 15 ? 
                            imageMaterials[index].name.substring(0, 12) + '...' : 
                            imageMaterials[index].name }}
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <v-text-field 
                          v-model="imageTitles[index]" 
                          label="Title" 
                          variant="outlined" 
                          density="compact"
                        ></v-text-field>
                        <v-select 
                          v-model="imageTypes[index]" 
                          :items="materialTypes" 
                          label="Material Type" 
                          variant="outlined" 
                          density="compact"
                        ></v-select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-stepper-window-item>
            
            <v-stepper-window-item value="4">
              <!-- 3D Model -->
              <div class="create-section">
                <h3 class="text-subtitle-1 font-weight-bold mb-4">VIS 3D Model (Optional)</h3>

                <!-- Existing 3D Model (Edit Mode) -->
                <div v-if="isEditMode && existing3DModel" class="mb-4">
                  <h4 class="text-subtitle-2 font-weight-medium mb-3">Current 3D Model</h4>
                  <v-card class="pa-3 mb-4">
                    <div class="d-flex justify-space-between align-center">
                      <div>
                        <div class="text-subtitle-2">{{ existing3DModel.title }}</div>
                        <div class="text-caption">{{ formatMaterialType(existing3DModel.material_type) }}</div>
                      </div>
                      <div class="d-flex align-center">
                        <action-button
                          variant="text"
                          left-icon="mdi-download"
                          @click="openExternalLink(existing3DModel.model_3d)"
                          size="small"
                          class="mr-2"
                        >
                          Download
                        </action-button>
                        <action-button
                          variant="text"
                          right-icon="mdi-delete"
                          @click="removeExisting3DModel"
                          size="small"
                        >
                          Remove
                        </action-button>
                      </div>
                    </div>
                  </v-card>
                </div>
                
                <v-file-input
                  v-model="model3d"
                  :label="isEditMode && existing3DModel ? 'Replace 3D Model' : 'Upload 3D Model'"
                  variant="outlined"
                  accept=".glb,.gltf"
                  prepend-icon="mdi-cube-outline"
                  show-size
                  hint="Supported formats: GLB, GLTF"
                  persistent-hint
                ></v-file-input>
                
                <div v-if="model3d || (isEditMode && existing3DModel)">
                  <h4 class="text-subtitle-2 font-weight-medium mt-4 mb-2">Model Settings</h4>
                  <v-checkbox
                    v-model="model3dOptions.is_interactive"
                    label="Interactive Model"
                  ></v-checkbox>
                  
                  <v-checkbox
                    v-model="model3dOptions.allow_rotation"
                    label="Allow Rotation"
                  ></v-checkbox>
                  
                  <v-checkbox
                    v-model="model3dOptions.allow_zoom"
                    label="Allow Zoom"
                  ></v-checkbox>
                </div>
              </div>
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-card-text>
      
      <v-divider></v-divider>
      
      <v-card-actions class="pa-4">
        <action-button
          v-if="currentStep !== '1'"
          variant="text"
          @click="currentStep = String(Number(currentStep) - 1)"
        >
          Previous
        </action-button>
        <v-spacer></v-spacer>
        <action-button
          v-if="currentStep !== '4'"
          variant="primary"
          @click="handleNext"
        >
          Next
        </action-button>
        <action-button
          v-else
          variant="primary"
          :loading="submitting"
          :disabled="submitting"
          @click="submitForm"
        >
          {{ isEditMode ? 'Update VIS' : 'Create VIS' }}
        </action-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ActionButton from '@/components/common/ActionButton.vue';
import apiService from '@/services/apiService';

export default {
  name: 'VISFormModal',
  components: {
    ActionButton
  },
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    visData: {
      type: Object,
      default: null
    },
    isEditMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      internalShowModal: false,
      currentStep: "1",
      submitting: false,
      dateMenu: false,
      
      // Brand Selection
      brandSelection: 'existing',
      brands: [],
      brandsLoading: false,
      
      // New Brand
      newBrand: {
        name: '',
        description: '',
        logo: null
      },
      
      // Geographical Regions
      regions: [],
      regionsLoading: false,
      
      // Form Data
      formData: {
        brand: null,
        version: '',
        release_date: new Date().toISOString().substr(0, 10),
        document: null,
        category: '',
        market: '',
        geographical_footprint: []
      },
      
      // Image Materials
      imageMaterials: [],
      imagePreviewUrls: [],
      imageTitles: [],
      imageTypes: [],
      existingImages: [],
      deletedImageIds: [],
      materialTypes: [
        { value: '2d_graphics', title: '2D Graphics' },
        { value: '3d_graphics', title: '3D Graphics' },
        { value: 'renders', title: 'Renders' },
        { value: 'mockups', title: 'Mockups' },
        { value: 'package_variations', title: 'Package Variations' },
        { value: 'on_shelf_mockups', title: 'On-Shelf Mockups' },
        { value: 'retail_images', title: 'Retail Images' },
        { value: 'competitor', title: 'Competitor' }
      ],
      
      // 3D Model
      model3d: null,
      existing3DModel: null,
      model3dOptions: {
        is_interactive: false,
        allow_rotation: false,
        allow_zoom: false
      },
      
      // Validation
      validationErrors: {},
      
      // Drag and Drop
      isDragOver: false
    };
  },
  watch: {
    showModal(newVal) {
      this.internalShowModal = newVal;
      if (newVal) {
        this.initializeModal();
      }
    },
    internalShowModal(newVal) {
      if (!newVal) {
        this.$emit('close');
      }
    },
    visData: {
      immediate: true,
      handler(newData) {
        if (newData && this.isEditMode) {
          this.populateFormWithVisData(newData);
        }
      }
    }
  },
  methods: {
    async initializeModal() {
      this.currentStep = "1";
      this.resetForm();
      await this.fetchBrands();
      await this.fetchRegions();
      
      if (this.isEditMode && this.visData) {
        this.populateFormWithVisData(this.visData);
      }
    },
    
    resetForm() {
      this.currentStep = "1";
      this.brandSelection = 'existing';
      this.newBrand = {
        name: '',
        description: '',
        logo: null
      };
      this.formData = {
        brand: null,
        version: '',
        release_date: new Date().toISOString().substr(0, 10),
        document: null,
        category: '',
        market: '',
        geographical_footprint: []
      };
      this.imageMaterials = [];
      this.imagePreviewUrls = [];
      this.imageTitles = [];
      this.imageTypes = [];
      this.existingImages = [];
      this.deletedImageIds = [];
      this.model3d = null;
      this.existing3DModel = null;
      this.model3dOptions = {
        is_interactive: false,
        allow_rotation: false,
        allow_zoom: false
      };
      this.validationErrors = {};
      this.isDragOver = false;
    },
    
    async populateFormWithVisData(visData) {
      // Populate basic form data
      this.formData = {
        brand: visData.brand?.id || visData.brand,
        version: visData.version || '',
        release_date: visData.release_date || new Date().toISOString().substr(0, 10),
        document: null, // Don't populate existing document
        category: visData.category || visData.brand_positioning || '',
        market: visData.market || visData.category_insights || '',
        geographical_footprint: visData.geographical_footprint?.map(region => region.id || region) || []
      };
      
      // Fetch and populate materials
      if (visData.id) {
        try {
          const response = await apiService.getVisMaterials(visData.id);
          const materials = response.data?.results || response.data || [];
          
          // Separate images and 3D models
          this.existingImages = materials.filter(material => 
            material.material_type !== '3d_graphics' || !material.model_3d
          );
          
          this.existing3DModel = materials.find(material => 
            material.material_type === '3d_graphics' && material.model_3d
          );
          
          // Populate 3D model options if available
          if (this.existing3DModel) {
            this.model3dOptions = {
              is_interactive: this.existing3DModel.is_interactive || false,
              allow_rotation: this.existing3DModel.allow_rotation || false,
              allow_zoom: this.existing3DModel.allow_zoom || false
            };
          }
        } catch (error) {
          console.error('Error fetching VIS materials:', error);
        }
      }
    },
    
    closeModal() {
      this.internalShowModal = false;
    },
    
    // API calls
    async fetchBrands() {
      this.brandsLoading = true;
      try {
        const response = await apiService.getBrands();
        this.brands = response.data.results || response.data;
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        this.brandsLoading = false;
      }
    },
    
    async fetchRegions() {
      this.regionsLoading = true;
      try {
        const response = await apiService.getGeographicalRegions();
        this.regions = response.data.results || response.data;
      } catch (error) {
        console.error('Error fetching geographical regions:', error);
      } finally {
        this.regionsLoading = false;
      }
    },
    
    async createBrand() {
      if (!this.validateBrandForm()) {
        return null;
      }
      
      try {
        const formData = new FormData();
        formData.append('name', this.newBrand.name);
        
        if (this.newBrand.description) {
          formData.append('description', this.newBrand.description);
        }
        
        if (this.newBrand.logo) {
          formData.append('logo', this.newBrand.logo);
        }
        
        const response = await apiService.post('/brands/brands/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        return response.data;
      } catch (error) {
        console.error('Error creating brand:', error);
        if (error.response && error.response.data) {
          this.handleValidationErrors(error.response.data, 'brand');
        }
        return null;
      }
    },
    
    // Image handling methods
    openImageFileDialog() {
      this.$refs.imageFileInput.click();
    },
    
    handleImageFileSelect(event) {
      const files = Array.from(event.target.files);
      if (!files.length) return;
      
      this.addImageFiles(files);
    },
    
    // Drag and Drop methods
    handleDragEnter(event) {
      event.preventDefault();
      this.isDragOver = true;
    },
    
    handleDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },
    
    handleDragLeave(event) {
      event.preventDefault();
      // Only set isDragOver to false if we're leaving the drop zone entirely
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isDragOver = false;
      }
    },
    
    handleDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      
      const files = Array.from(event.dataTransfer.files);
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      
      if (imageFiles.length > 0) {
        this.addImageFiles(imageFiles);
      }
      
      if (files.length > imageFiles.length) {
        alert('Some files were not added because they are not image files.');
      }
    },
    
    addImageFiles(files) {
      this.imageMaterials = [...this.imageMaterials, ...files];
      this.clearValidationError('images');
      this.previewImages();
    },
    
    removeImage(index) {
      this.imageMaterials.splice(index, 1);
      this.imagePreviewUrls.splice(index, 1);
      this.imageTitles.splice(index, 1);
      this.imageTypes.splice(index, 1);
    },
    
    removeExistingImage(index) {
      const image = this.existingImages[index];
      this.deletedImageIds.push(image.id);
      this.existingImages.splice(index, 1);
    },
    
    removeExisting3DModel() {
      if (this.existing3DModel) {
        this.deletedImageIds.push(this.existing3DModel.id);
        this.existing3DModel = null;
      }
    },
    
    previewImages() {
      // Clear existing previews for new images
      const newImageCount = this.imageMaterials.length;
      this.imagePreviewUrls = this.imagePreviewUrls.slice(0, newImageCount);
      this.imageTitles = this.imageTitles.slice(0, newImageCount);
      this.imageTypes = this.imageTypes.slice(0, newImageCount);
      
      for (let i = this.imagePreviewUrls.length; i < newImageCount; i++) {
        const file = this.imageMaterials[i];
        const url = URL.createObjectURL(file);
        this.imagePreviewUrls.push(url);
        this.imageTitles.push(file.name.split('.')[0] || `Image ${i + 1}`);
        this.imageTypes.push('2d_graphics'); // Default type
      }
    },
    
    // Helper methods
    getInitials(name) {
      if (!name) return '';
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase();
    },
    
    formatMaterialType(type) {
      if (!type) return 'Unknown Type';
      
      const typeMap = {
        '2d_graphics': '2D Graphics',
        '3d_graphics': '3D Graphics',
        'renders': 'Renders',
        'mockups': 'Mockups',
        'package_variations': 'Package Variations',
        'on_shelf_mockups': 'On-Shelf Mockups',
        'retail_images': 'Retail Images',
        'competitor': 'Competitor'
      };
      
      return typeMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    
    openExternalLink(url, target = '_blank') {
      if (!url) return;
      window.open(url, target);
    },
    
    // Form submission
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }
      
      this.submitting = true;
      
      try {
        if (this.isEditMode) {
          await this.updateVIS();
        } else {
          await this.createVIS();
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        if (error.response && error.response.data) {
          this.handleValidationErrors(error.response.data);
        } else {
          alert(`Error ${this.isEditMode ? 'updating' : 'creating'} Visual Identity System. Please try again.`);
        }
      } finally {
        this.submitting = false;
      }
    },
    
    async createVIS() {
      // Handle brand creation if needed
      let brandId = this.formData.brand;
      
      if (this.brandSelection === 'new') {
        const createdBrand = await this.createBrand();
        if (!createdBrand) {
          return;
        }
        brandId = createdBrand.id;
      }
      
      // Create VIS
      const formData = new FormData();
      formData.append('brand', brandId);
      formData.append('version', this.formData.version);
      
      // Ensure date is in YYYY-MM-DD format
      const releaseDate = this.formData.release_date;
      if (releaseDate instanceof Date) {
        formData.append('release_date', releaseDate.toISOString().substr(0, 10));
      } else if (typeof releaseDate === 'string') {
        // If it's already a string, ensure it's in the right format
        const dateObj = new Date(releaseDate);
        formData.append('release_date', dateObj.toISOString().substr(0, 10));
      } else {
        formData.append('release_date', releaseDate);
      }
      
      if (this.formData.document) {
        formData.append('document', this.formData.document);
      }

      if (!this.formData.category) {
        this.validationErrors.category = ['Category is required'];
        return;
      }
      formData.append('category', this.formData.category);

      if (!this.formData.market) {
        this.validationErrors.market = ['Market is required'];
        return;
      }
      formData.append('market', this.formData.market);

      if (this.formData.geographical_footprint.length === 0) {
        this.validationErrors.geographical_footprint = ['At least one geographical region is required'];
        return;
      }
      this.formData.geographical_footprint.forEach(regionId => {
        formData.append('geographical_footprint', regionId);
      });
      
      // Create the VIS
      const visResponse = await apiService.createVisualIdentitySystem(formData);
      const visId = visResponse.data.id;
      
      // Upload materials and handle 3D model
      await this.uploadMaterials(visId);
      
      // Success
      this.closeModal();
      this.$emit('success', visResponse.data);
    },
    
    async updateVIS() {
      const visId = this.visData.id;
      
      // Update VIS basic information
      const formData = new FormData();
      
      // Always include brand ID for updates (even though it can't be changed)
      formData.append('brand', this.formData.brand);
      formData.append('version', this.formData.version);
      
      // Ensure date is in YYYY-MM-DD format
      const releaseDate = this.formData.release_date;
      if (releaseDate instanceof Date) {
        formData.append('release_date', releaseDate.toISOString().substr(0, 10));
      } else if (typeof releaseDate === 'string') {
        // If it's already a string, ensure it's in the right format
        const dateObj = new Date(releaseDate);
        formData.append('release_date', dateObj.toISOString().substr(0, 10));
      } else {
        formData.append('release_date', releaseDate);
      }
      
      // Document is optional for updates
      if (this.formData.document) {
        formData.append('document', this.formData.document);
      }

      if (!this.formData.category) {
        this.validationErrors.category = ['Category is required'];
        return;
      }
      formData.append('category', this.formData.category);

      if (!this.formData.market) {
        this.validationErrors.market = ['Market is required'];
        return;
      }
      formData.append('market', this.formData.market);

      if (this.formData.geographical_footprint.length === 0) {
        this.validationErrors.geographical_footprint = ['At least one geographical region is required'];
        return;
      }
      this.formData.geographical_footprint.forEach(regionId => {
        formData.append('geographical_footprint', regionId);
      });
      
      // Update the VIS
      const visResponse = await apiService.updateVisualIdentitySystem(visId, formData);
      
      // Delete removed materials
      for (const deletedId of this.deletedImageIds) {
        try {
          await apiService.delete(`/brands/vis-materials/${deletedId}/`);
        } catch (error) {
          console.error('Error deleting material:', error);
        }
      }
      
      // Upload new materials
      await this.uploadMaterials(visId);
      
      // Success
      this.closeModal();
      this.$emit('success', visResponse.data);
    },
    
    async uploadMaterials(visId) {
      // Upload new images
      if (this.imageMaterials.length > 0) {
        const materialPromises = [];
        
        for (let i = 0; i < this.imageMaterials.length; i++) {
          const materialData = new FormData();
          materialData.append('vis', visId);
          materialData.append('title', this.imageTitles[i] || `Image ${i + 1}`);
          materialData.append('material_type', this.imageTypes[i] || '2d_graphics');
          materialData.append('image', this.imageMaterials[i]);
          
          materialPromises.push(
            apiService.post('/brands/vis-materials/', materialData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
          );
        }
        
        await Promise.all(materialPromises);
      }
      
      // Upload 3D model if provided
      if (this.model3d) {
        const modelData = new FormData();
        modelData.append('vis', visId);
        modelData.append('title', 'VIS 3D Model');
        modelData.append('material_type', '3d_graphics');
        modelData.append('model_3d', this.model3d);
        modelData.append('is_interactive', this.model3dOptions.is_interactive);
        modelData.append('allow_rotation', this.model3dOptions.allow_rotation);
        modelData.append('allow_zoom', this.model3dOptions.allow_zoom);
        
        await apiService.post('/brands/vis-materials/', modelData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
    },
    
    // Form validation
    validateBrandForm() {
      let isValid = true;
      this.validationErrors = {};
      
      if (!this.newBrand.name) {
        this.validationErrors.brandName = ['Brand name is required'];
        isValid = false;
      }
      
      return isValid;
    },
    
    validateForm() {
      let isValid = true;
      this.validationErrors = {};
      
      // Brand validation (only for create mode)
      if (!this.isEditMode && this.brandSelection === 'existing' && !this.formData.brand) {
        this.validationErrors.brand = ['Please select a brand'];
        isValid = false;
      }
      
      // VIS validation
      if (!this.formData.version) {
        this.validationErrors.version = ['Version is required'];
        isValid = false;
      }
      
      if (!this.formData.release_date) {
        this.validationErrors.release_date = ['Release date is required'];
        isValid = false;
      }

      if (!this.formData.category) {
        this.validationErrors.category = ['Category is required'];
        isValid = false;
      }

      if (!this.formData.market) {
        this.validationErrors.market = ['Market is required'];
        isValid = false;
      }

      if (this.formData.geographical_footprint.length === 0) {
        this.validationErrors.geographical_footprint = ['At least one geographical region is required'];
        isValid = false;
      }
      
      // Document validation (required for new VIS, optional for updates)
      if (!this.isEditMode && !this.formData.document) {
        this.validationErrors.document = ['VIS document is required'];
        isValid = false;
      }
      
      // Image materials validation
      const totalImages = (this.imageMaterials?.length || 0) + (this.existingImages?.length || 0);
      if (totalImages === 0) {
        this.validationErrors.images = ['At least one image is required'];
        isValid = false;
      }
      
      return isValid;
    },
    
    handleValidationErrors(errors, prefix = '') {
      for (const field in errors) {
        const errorField = prefix ? `${prefix}${field.charAt(0).toUpperCase() + field.slice(1)}` : field;
        this.validationErrors[errorField] = errors[field];
      }
    },
    
    clearValidationError(field) {
      if (this.validationErrors[field]) {
        delete this.validationErrors[field];
      }
    },
    
    // Stepper navigation with validation
    handleNext() {
      // Validation before moving to the next step
      if (this.currentStep === '2') {
        // Ensure version is provided
        if (!this.formData.version) {
          this.validationErrors.version = ['Version is required'];
          return;
        }
        // For edit mode, document is optional
        if (!this.isEditMode && !this.formData.document) {
          this.validationErrors.document = ['VIS document is required'];
          return;
        }
        // Validate category
        if (!this.formData.category) {
          this.validationErrors.category = ['Category is required'];
          return;
        }
        // Validate market
        if (!this.formData.market) {
          this.validationErrors.market = ['Market is required'];
          return;
        }
        // Validate geographical footprint
        if (this.formData.geographical_footprint.length === 0) {
          this.validationErrors.geographical_footprint = ['At least one geographical region is required'];
          return;
        }
      } else if (this.currentStep === '3') {
        // Check total images (new + existing)
        const totalImages = (this.imageMaterials?.length || 0) + (this.existingImages?.length || 0);
        if (totalImages === 0) {
          this.validationErrors.images = ['At least one image is required'];
          return;
        }
      }

      // Clear any validation errors and advance
      this.clearValidationError('version');
      this.clearValidationError('document');
      this.clearValidationError('category');
      this.clearValidationError('market');
      this.clearValidationError('geographical_footprint');
      this.clearValidationError('images');
      this.currentStep = String(Number(this.currentStep) + 1);
    }
  }
};
</script>

<style scoped>
.create-section {
  padding: 16px;
}

.new-brand-form {
  margin-top: 16px;
}

.image-list-item {
  border-bottom: 1px solid #eee;
}

.image-list-item:last-child {
  border-bottom: none;
}

.image-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 12px;
}

.image-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-preview-sm {
  width: 80px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #eee;
}

.image-preview-sm img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-details-card {
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.avatar-initials {
  font-weight: 600;
  font-size: 0.8rem;
}

.drag-drop-zone {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 32px 16px;
  text-align: center;
  transition: all 0.3s ease;
  background-color: #fafafa;
  cursor: pointer;
}

.drag-drop-zone:hover {
  border-color: #e94057;
  background-color: #f5f5f5;
}

.drag-over {
  border-color: #e94057 !important;
  background-color: rgba(233, 64, 87, 0.05) !important;
  transform: scale(1.02);
}

.has-files {
  border-color: #4caf50;
  background-color: rgba(76, 175, 80, 0.05);
}

.drag-drop-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
}
</style> 